// Confidence-Based Case Routing Configuration
// Task #9: Define Confidence Filtering Architecture - Routing Rules

import {
  CONFIDENCE_ARCHITECTURE,
  ConfidenceAssessment,
  CaseFlagging,
} from '../types/confidenceArchitecture';
import { PayrollCase } from '../types';

// Routing workflow definitions
export const ROUTING_WORKFLOWS = {
  // Auto-processing workflow (95%+ confidence)
  AUTO_PROCESS: {
    name: 'Automated Processing',
    description: 'Cases with 95%+ confidence are automatically processed',
    steps: [
      'validate_business_rules',
      'auto_extract_data',
      'auto_submit_to_core',
      'monitor_processing',
      'auto_complete',
    ],
    csrInvolvement: false,
    maxProcessingTime: '2_minutes',
    fallbackWorkflow: 'HIGH_CONFIDENCE',
  },

  // High confidence workflow (90-94% confidence)
  HIGH_CONFIDENCE: {
    name: 'Quick Review',
    description: 'High confidence cases require minimal CSR review',
    steps: [
      'present_to_csr',
      'quick_data_review',
      'approve_or_edit',
      'submit_to_core',
      'complete',
    ],
    csrInvolvement: true,
    maxProcessingTime: '5_minutes',
    fallbackWorkflow: 'MEDIUM_CONFIDENCE',
  },

  // Medium confidence workflow (75-89% confidence)
  MEDIUM_CONFIDENCE: {
    name: 'Standard Review',
    description: 'Medium confidence cases require standard CSR validation',
    steps: [
      'present_to_csr',
      'review_extracted_data',
      'validate_against_documents',
      'edit_if_needed',
      'submit_to_core',
      'complete',
    ],
    csrInvolvement: true,
    maxProcessingTime: '15_minutes',
    fallbackWorkflow: 'LOW_CONFIDENCE',
  },

  // Low confidence workflow (60-74% confidence)
  LOW_CONFIDENCE: {
    name: 'Enhanced Review',
    description: 'Low confidence cases require detailed CSR validation',
    steps: [
      'present_to_csr',
      'detailed_data_review',
      'cross_reference_documents',
      'validate_business_rules',
      'manual_data_entry_if_needed',
      'supervisor_approval',
      'submit_to_core',
      'complete',
    ],
    csrInvolvement: true,
    maxProcessingTime: '30_minutes',
    fallbackWorkflow: 'MANUAL_ESCALATION',
  },

  // Critical low confidence workflow (<60% confidence)
  MANUAL_ESCALATION: {
    name: 'Manual Escalation',
    description: 'Critical low confidence cases are escalated immediately',
    steps: [
      'flag_to_dynamics',
      'notify_supervisor',
      'create_manual_task',
      'await_manual_processing',
    ],
    csrInvolvement: false,
    maxProcessingTime: 'indefinite',
    fallbackWorkflow: null,
  },
} as const;

// Flagging scenarios and their handling
export const FLAGGING_SCENARIOS = {
  BELOW_VISIBILITY_THRESHOLD: {
    trigger: 'confidence < 85',
    priority: 'high' as const,
    action: 'reprocess_or_manual',
    dynamicsFlag: {
      reason: 'Confidence below visibility threshold',
      recommendedAction: 'Improve document quality or switch to manual processing',
      reprocessingAllowed: true,
    },
  },

  CRITICAL_CONFIDENCE: {
    trigger: 'confidence < 60',
    priority: 'critical' as const,
    action: 'immediate_escalation',
    dynamicsFlag: {
      reason: 'Critical confidence level - immediate attention required',
      recommendedAction: 'Manual processing required',
      reprocessingAllowed: false,
    },
  },

  BUSINESS_RULE_VIOLATION: {
    trigger: 'business_validation_failed',
    priority: 'high' as const,
    action: 'flag_and_review',
    dynamicsFlag: {
      reason: 'Business rule violations detected',
      recommendedAction: 'Review and correct data before reprocessing',
      reprocessingAllowed: true,
    },
  },

  PROCESSING_ERROR: {
    trigger: 'processing_failed',
    priority: 'medium' as const,
    action: 'retry_or_escalate',
    dynamicsFlag: {
      reason: 'Technical processing error occurred',
      recommendedAction: 'System retry or manual intervention',
      reprocessingAllowed: true,
    },
  },

  DATA_INTEGRITY_FAILURE: {
    trigger: 'data_integrity_check_failed',
    priority: 'high' as const,
    action: 'flag_and_manual_review',
    dynamicsFlag: {
      reason: 'Data integrity issues detected',
      recommendedAction: 'Manual data verification required',
      reprocessingAllowed: false,
    },
  },
} as const;

// CSR notification rules based on confidence levels
export const CSR_NOTIFICATION_RULES = {
  AUTO_PROCESS: {
    notifyOnStart: false,
    notifyOnComplete: true,
    notifyOnError: true,
    dashboardAlert: false,
  },
  HIGH_CONFIDENCE: {
    notifyOnStart: true,
    notifyOnComplete: true,
    notifyOnError: true,
    dashboardAlert: true,
    alertType: 'info' as const,
  },
  MEDIUM_CONFIDENCE: {
    notifyOnStart: true,
    notifyOnComplete: true,
    notifyOnError: true,
    dashboardAlert: true,
    alertType: 'warning' as const,
  },
  LOW_CONFIDENCE: {
    notifyOnStart: true,
    notifyOnComplete: true,
    notifyOnError: true,
    dashboardAlert: true,
    alertType: 'warning' as const,
    requireSupervisorNotification: true,
  },
  CRITICAL: {
    notifyOnStart: true,
    notifyOnComplete: false, // Won't complete normally
    notifyOnError: true,
    dashboardAlert: true,
    alertType: 'error' as const,
    requireSupervisorNotification: true,
    escalateImmediately: true,
  },
} as const;

// Quality assurance checkpoints
export const QA_CHECKPOINTS = {
  PRE_PROCESSING: {
    name: 'Pre-Processing Validation',
    checks: [
      'document_quality_check',
      'required_fields_validation',
      'format_compatibility_check',
    ],
    confidenceThreshold: 70,
    failureAction: 'flag_for_reprocessing',
  },

  POST_EXTRACTION: {
    name: 'Post-Extraction Validation',
    checks: [
      'data_completeness_check',
      'business_rule_validation',
      'cross_field_validation',
    ],
    confidenceThreshold: 75,
    failureAction: 'lower_confidence_and_flag',
  },

  PRE_SUBMISSION: {
    name: 'Pre-Submission Validation',
    checks: [
      'final_data_integrity_check',
      'calculation_verification',
      'compliance_validation',
    ],
    confidenceThreshold: 80,
    failureAction: 'prevent_submission_and_escalate',
  },
} as const;

// Performance monitoring configuration
export const PERFORMANCE_MONITORING = {
  METRICS_TO_TRACK: [
    'confidence_accuracy_rate',
    'processing_time_by_confidence',
    'csr_override_frequency',
    'flagging_rate_by_type',
    'successful_completion_rate',
  ],

  ALERT_THRESHOLDS: {
    low_accuracy_rate: 0.85, // Alert if accuracy drops below 85%
    high_flagging_rate: 0.15, // Alert if flagging rate exceeds 15%
    processing_time_exceeded: 1.5, // Alert if processing takes 1.5x expected time
  },

  REPORTING_INTERVALS: {
    real_time_dashboard: '1_minute',
    hourly_summary: '1_hour',
    daily_report: '24_hours',
    weekly_analysis: '7_days',
  },
} as const;

// Main routing function that determines the workflow for a case
export const routeCase = (
  caseItem: PayrollCase,
  assessment: ConfidenceAssessment
): {
  workflow: keyof typeof ROUTING_WORKFLOWS;
  notifications: (typeof CSR_NOTIFICATION_RULES)[keyof typeof CSR_NOTIFICATION_RULES];
  qaCheckpoints: (keyof typeof QA_CHECKPOINTS)[];
  flagging?: CaseFlagging;
} => {
  let workflow: keyof typeof ROUTING_WORKFLOWS;

  // Determine workflow based on confidence level
  switch (assessment.level) {
    case 'auto_process':
      workflow = 'AUTO_PROCESS';
      break;
    case 'high':
      workflow = 'HIGH_CONFIDENCE';
      break;
    case 'medium':
      workflow = 'MEDIUM_CONFIDENCE';
      break;
    case 'low':
      workflow = 'LOW_CONFIDENCE';
      break;
    case 'critical':
      workflow = 'MANUAL_ESCALATION';
      break;
    default:
      workflow = 'MEDIUM_CONFIDENCE';
  }

  // Get notification rules
  const notificationKey =
    assessment.level === 'auto_process'
      ? 'AUTO_PROCESS'
      : assessment.level === 'high'
        ? 'HIGH_CONFIDENCE'
        : assessment.level === 'medium'
          ? 'MEDIUM_CONFIDENCE'
          : assessment.level === 'low'
            ? 'LOW_CONFIDENCE'
            : 'CRITICAL';

  const notifications = CSR_NOTIFICATION_RULES[notificationKey];

  // Determine required QA checkpoints
  const qaCheckpoints: (keyof typeof QA_CHECKPOINTS)[] = ['PRE_PROCESSING'];

  if (assessment.level !== 'critical') {
    qaCheckpoints.push('POST_EXTRACTION');
  }

  if (assessment.level === 'auto_process' || assessment.level === 'high') {
    qaCheckpoints.push('PRE_SUBMISSION');
  }

  // Generate flagging if required
  let flagging: CaseFlagging | undefined;
  if (assessment.requiresFlag) {
    flagging = {
      caseId: caseItem.caseNumber,
      reason: assessment.flagReason || 'Confidence-based flagging',
      priority: assessment.level === 'critical' ? 'critical' : 'high',
      timestamp: new Date().toISOString(),
      confidence: assessment.score,
      flagType: assessment.level === 'critical' ? 'escalation' : 'reprocessing',
      metadata: {
        originalConfidence: caseItem.aiConfidence,
        processingAttempts: 1,
        lastError: assessment.flagReason,
      },
    };
  }

  return {
    workflow,
    notifications,
    qaCheckpoints,
    flagging,
  };
};

// Function to generate routing summary for a case
export const generateRoutingSummary = (
  caseItem: PayrollCase,
  assessment: ConfidenceAssessment
) => {
  const routing = routeCase(caseItem, assessment);
  const workflowInfo = ROUTING_WORKFLOWS[routing.workflow];

  return {
    caseNumber: caseItem.caseNumber,
    confidence: assessment.score,
    level: assessment.level,
    workflow: routing.workflow,
    estimatedTime: workflowInfo.maxProcessingTime,
    csrRequired: workflowInfo.csrInvolvement,
    qaCheckpoints: routing.qaCheckpoints.length,
    requiresFlagging: !!routing.flagging,
    summary: `${workflowInfo.name}: ${workflowInfo.description}`,
  };
};
